import { useState } from 'react';
import { useLocalization, type Language } from '../../../../../config/localization';
import { getWorkflowUrlRole } from '../../../../../utils/workflowUrlRoles';
import CopyValueButton from '../../../../ui/CopyValueButton';
import Icon, { ICONS } from '../../../../ui/icons';
import SearchablePromptSelect, { type PromptOption } from '../../../../ui/SearchablePromptSelect';

/**
 * Draft form state for one workflow URL. Mirrors the shape ApiEndpointsSection
 * kept in its inline modal: `prompt_id` may transiently hold the string value
 * emitted by SearchablePromptSelect — callers convert with Number() on save.
 */
export interface WorkflowUrlDraft {
  id?: number;
  url_index: number;
  url: string;
  api_key: string;
  description: string;
  prompt_id?: number | string | null;
  dify_workflow_url?: string;
  extra_params?: Record<string, string>;
}

interface WorkflowUrlEditModalProps {
  value: WorkflowUrlDraft;
  onChange: (next: WorkflowUrlDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  prompts: PromptOption[];
  promptsLoading?: boolean;
  saving?: boolean;
  language: Language;
}

/**
 * Controlled edit/create modal for a single workflow URL (Dify endpoint).
 * Shared by ApiEndpointsSection and the enhancer summary in PipelineStepsSection
 * so both edit paths stay one implementation.
 */
export default function WorkflowUrlEditModal({
  value,
  onChange,
  onSave,
  onCancel,
  prompts,
  promptsLoading = false,
  saving = false,
  language,
}: WorkflowUrlEditModalProps) {
  const t = useLocalization(language);
  const [showApiKey, setShowApiKey] = useState(false);

  const role = getWorkflowUrlRole(value.url_index);
  const saveDisabled = saving || !value.url.trim() || !value.description.trim();

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => {
        e.stopPropagation();
        onCancel();
      }}
    >
      <div
        className="bg-gray-900 border border-gray-600 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-700 sticky top-0 bg-gray-900">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icon name={ICONS.LINK} size={20} className="text-blue-400" />
            {value.id
              ? (t.adminWorkflows?.urlManager?.editUrlTitle || 'Edit URL')
              : (t.adminWorkflows?.urlManager?.addUrlTitle || 'Add URL')
            }
          </h3>
        </div>

        <div className="p-6 space-y-4">
          {role && (
            <p className="rounded-lg border border-cyan-500/20 bg-cyan-900/10 px-3 py-2 text-sm text-cyan-300">
              {role === 'translate'
                ? (t.adminWorkflows?.urlManager?.roleHintTranslate || 'This URL is used to translate the prompt')
                : (t.adminWorkflows?.urlManager?.roleHintPromptEnhance || 'This URL is used to enhance the prompt')}
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.adminWorkflows?.urlManager?.description || 'Description'}
            </label>
            <input
              type="text"
              value={value.description}
              onChange={(e) => onChange({ ...value, description: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder={t.adminWorkflows?.urlManager?.descriptionPlaceholder || 'Enter URL description...'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.adminWorkflows?.urlManager?.apiUrl || 'API URL'}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={value.url}
                onChange={(e) => onChange({ ...value, url: e.target.value })}
                className="min-w-0 flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="https://api.loremax.ai/v1/workflows/run"
              />
              <CopyValueButton
                value={value.url}
                title={t.adminWorkflows?.urlManager?.copy || 'Copy'}
                copiedTitle={t.adminWorkflows?.urlManager?.copied || 'Copied'}
                className="shrink-0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.adminWorkflows?.urlManager?.difyWorkflowUrl || 'Dify Workflow URL'}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={value.dify_workflow_url || ''}
                onChange={(e) => onChange({ ...value, dify_workflow_url: e.target.value })}
                className="min-w-0 flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="https://api.loremax.ai/app/xxxxxxxxx/workflow"
              />
              <CopyValueButton
                value={value.dify_workflow_url || ''}
                title={t.adminWorkflows?.urlManager?.copy || 'Copy'}
                copiedTitle={t.adminWorkflows?.urlManager?.copied || 'Copied'}
                className="shrink-0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.adminWorkflows?.urlManager?.apiKey || 'API Key'}
            </label>
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={value.api_key}
                  onChange={(e) => onChange({ ...value, api_key: e.target.value })}
                  className="w-full px-3 py-2 pr-10 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder={t.adminWorkflows?.urlManager?.apiKeyPlaceholder || 'Enter API key...'}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey((prev) => !prev)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  <Icon name={showApiKey ? ICONS.EYE_OFF : ICONS.EYE} size={16} />
                </button>
              </div>
              <CopyValueButton
                value={value.api_key}
                title={t.adminWorkflows?.urlManager?.copy || 'Copy'}
                copiedTitle={t.adminWorkflows?.urlManager?.copied || 'Copied'}
                className="shrink-0"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t.adminWorkflows?.urlManager?.apiKeyNote || 'Leave empty if the API key is included in the URL as a query parameter'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.admin?.pipelineStepsConfig?.workflowPrompt || 'Workflow Prompt'}
            </label>
            <SearchablePromptSelect
              value={value.prompt_id != null ? String(value.prompt_id) : null}
              onChange={(promptId) => onChange({ ...value, prompt_id: promptId })}
              prompts={prompts}
              placeholder={t.adminWorkflows?.urlManager?.workflowPromptPlaceholder || 'Select workflow prompt...'}
              disabled={promptsLoading}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t.adminWorkflows?.urlManager?.workflowPromptNote || 'Optional: Select a workflow prompt to use with this URL'}
            </p>
          </div>

          {/* Extra Parameters Section */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.adminWorkflows?.urlManager?.extraParameters || 'Extra Parameters'}
            </label>
            <div className="space-y-2">
              {Object.entries(value.extra_params || {}).map(([key, paramValue], index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={key}
                    onChange={(e) => {
                      const newParams = { ...value.extra_params };
                      delete newParams[key];
                      newParams[e.target.value] = paramValue;
                      onChange({ ...value, extra_params: newParams });
                    }}
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder={t.adminWorkflows?.urlManager?.parameterKey || 'Key'}
                  />
                  <input
                    type="text"
                    value={paramValue}
                    onChange={(e) => {
                      const newParams = { ...value.extra_params };
                      newParams[key] = e.target.value;
                      onChange({ ...value, extra_params: newParams });
                    }}
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder={t.adminWorkflows?.urlManager?.parameterValue || 'Value'}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newParams = { ...value.extra_params };
                      delete newParams[key];
                      onChange({ ...value, extra_params: newParams });
                    }}
                    className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    <Icon name={ICONS.TRASH} size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const newParams = { ...value.extra_params };
                  const newKey = `param${Object.keys(newParams).length + 1}`;
                  newParams[newKey] = '';
                  onChange({ ...value, extra_params: newParams });
                }}
                className="w-full py-2 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                <Icon name={ICONS.PLUS} size={16} />
                {t.adminWorkflows?.urlManager?.addParameter || 'Add Parameter'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t.adminWorkflows?.urlManager?.extraParametersNote || 'These parameters will be added to the request data'}
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-gray-700 flex justify-end gap-3 sticky bottom-0 bg-gray-900">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {t.adminWorkflows?.urlManager?.cancel || 'Cancel'}
          </button>
          <button
            onClick={onSave}
            disabled={saveDisabled}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            {saving
              ? (t.adminWorkflows?.urlManager?.saving || 'Saving...')
              : (t.adminWorkflows?.urlManager?.save || 'Save')
            }
          </button>
        </div>
      </div>
    </div>
  );
}
